<template>
  <div :class="['iot-page', { 'locale-ja': localeValue === 'ja' }]">
    <section class="hero">
      <img :src="getImageUrl(pr.heroImage)" :alt="pr.heroTitle" class="hero-bg" />
      <div class="inner hero-inner">
        <SIBreadcrumb
          :breadcrumb="pr.common.breadcrumb"
          :locale="localeValue"
          :show-products="false"
          :show-si="false"
          :current="pr.pageTitle || pr.heroTitle"
        />
        <h1>{{ pr.pageTitle || pr.heroTitle }}</h1>
        <p class="hero-subtitle">{{ pr.heroSubtitle }}</p>
        <p class="hero-tagline">{{ pr.heroTagline }}</p>
      </div>
    </section>

    <section class="intro">
      <div class="inner narrow">
        <div class="top-content">
          <img :src="require('@/assets/cooperate_logo_1.png')" alt="CooperateLogo" />
          <h5 v-viewportChanged="showView" class="hidden-element title">
            {{ pr.corporatePartnerships.title }}
          </h5>
          <component
            :is="'a'"
            target="_blank"
            :href="pr.corporatePartnerships.link.path"
            rel="noopener noreferrer"
            class="link-btn"
          >
            <span v-viewportChanged="showView" class="hidden-element">{{
              pr.corporatePartnerships.link.label
            }}</span>
            <img
              :src="require('@/assets/external_link_icon.svg')"
              alt="CooperateLogo"
              aria-hidden="true"
            />
          </component>
        </div>
        <p
          v-viewportChanged="showView"
          class="hidden-element"
          v-for="(line, index) in pr.intro"
          :key="`intro-${index}`"
        >
          {{ line }}
        </p>
      </div>
    </section>

    <section class="block light-bg">
      <div class="inner">
        <SISectionTitle :title="pr.scenarioTitle" :subtitle="pr.scenarioTitleEn" />
        <div v-viewportChanged="showView" class="hidden-element scenario-grid">
          <article v-for="item in pr.scenarios" :key="item.title" class="scenario-card">
            <div class="scene-image-wrap">
              <img :src="getImageUrl(item.image)" :alt="item.title" class="scene-image" />
            </div>
            <div class="card-body">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="block white-bg matrix-block">
      <div class="inner">
        <SISectionTitle :title="pr.matrixTitle" :subtitle="pr.matrixTitleEn" />
        <div v-viewportChanged="showView" class="hidden-element table-wrap">
          <table>
            <thead>
              <tr>
                <th>{{ pr.matrixHeadings.type }}</th>
                <th>{{ pr.matrixHeadings.mobility }}</th>
                <th>{{ pr.matrixHeadings.payload }}</th>
                <th>{{ pr.matrixHeadings.conn }}</th>
                <th>{{ pr.matrixHeadings.usage }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in pr.matrixRows"
                :key="row.type"
                :class="{ stripe: index % 2 === 1 }"
              >
                <td>{{ row.type }}</td>
                <td>{{ row.mobility }}</td>
                <td>{{ row.payload }}</td>
                <td>{{ row.conn }}</td>
                <td>{{ row.usage }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-viewportChanged="showView" class="hidden-element note">{{ pr.note }}</p>
      </div>
    </section>

    <section class="cta">
      <div class="inner cta-inner">
        <SISectionTitle :title="pr.common.cta" />
        <BaseButton tag="router-link" variant="dark" size="hero" :to="contactLink">
          <span v-viewportChanged="showView" class="hidden-element">
            {{ contactText }}
          </span>
        </BaseButton>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, inject } from 'vue';
import usePageResource from '@/composables/usePageResource';
import useRenderData from '@/composables/useRenderData';
import BaseButton from '@/components/common/BaseButton.vue';
import SIBreadcrumb from '@/components/si/SIBreadcrumb.vue';
import SISectionTitle from '@/components/si/SISectionTitle.vue';

export default {
  name: 'robot-page',
  components: {
    BaseButton,
    SIBreadcrumb,
    SISectionTitle,
  },
  setup() {
    const { pr, getImageUrl } = useRenderData('robot');
    const { pr: works } = usePageResource('works');
    const locale = inject('locale');
    const localeValue = computed(() => locale?.value || '');
    const contactText = computed(() => works.value.contact);
    const contactLink = computed(() => ({
      name: 'contact-us',
      params: {
        lang: localeValue.value || undefined,
      },
    }));

    return {
      pr,
      getImageUrl,
      localeValue,
      contactText,
      contactLink,
    };
  },
};
</script>

<style lang="scss" scoped>
.iot-page {
  background: #ffffff;
}

.iot-page.locale-ja {
  h1,
  h2,
  h3,
  h4,
  p,
  li,
  span,
  strong,
  td,
  th {
    word-break: normal;
    overflow-wrap: break-word;
    line-break: strict;
  }
}

.inner {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;
}

.narrow {
  max-width: 800px;

  .top-content {
    display: flex;
    flex-direction: column;
    gap: 40px;
    align-items: center;
    margin-top: 84px;
    margin-bottom: 102px;

    > img {
      max-width: 360px;
      width: 100%;
    }

    .title {
      color: #212121;
      text-align: center;
      font-size: 24px;
      font-weight: 700;
      line-height: 42px;
      letter-spacing: 0.396px;
    }

    .link-btn {
      display: flex;
      height: 50px;
      padding: 0 40px;
      background: #212121;
      color: #fff;
      letter-spacing: -0.15px;
      font-weight: 700;
      gap: 16px;
      justify-content: center;
      align-items: center;
      text-decoration: none;
      border-radius: 4px;

      > img {
        filter: brightness(0) invert(1);
        width: 24px;
      }
    }
  }
}

.hero {
  position: relative;
  min-height: 356px;
  display: flex;
  align-items: flex-end;

  .hero-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .hero-inner {
    position: relative;
    z-index: 1;
    padding-bottom: 48px;

    h1 {
      margin: 24px 0 0;
      font-size: 32px;
      line-height: 44px;
      color: #ffffff;
      font-weight: 700;
    }

    .hero-subtitle {
      margin: 8px 0 0;
      font-size: 16px;
      line-height: 24px;
      color: #a7aeb1;
    }

    .hero-tagline {
      margin: 12px 0 0;
      max-width: 600px;
      font-size: 16px;
      line-height: 28px;
      color: rgba(255, 255, 255, 0.8);
    }
  }
}

.intro {
  padding-bottom: 102px;

  p {
    margin: 0 0 16px;
    font-size: 16px;
    line-height: 30px;
    color: #222222;
    text-align: center;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.block {
  padding: 82px 0;

  &.light-bg {
    background: #f7f9fa;
  }

  &.white-bg {
    background: #ffffff;
  }
}

.scenario-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}

.scenario-card {
  border: 1px solid #e4e9eb;
  background: #ffffff;
  overflow: hidden;
  transition: transform 300ms cubic-bezier(0.165, 0.84, 0.44, 1),
    box-shadow 300ms cubic-bezier(0.165, 0.84, 0.44, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.1);
  }

  .scene-image-wrap {
    height: 160px;
    overflow: hidden;
  }

  .scene-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .card-body {
    padding: 24px;
  }

  h3 {
    margin: 0 0 8px;
    color: #212121;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: #666666;
    font-size: 14px;
    line-height: 24px;
  }
}

.matrix-block {
  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    min-width: 600px;
    border-collapse: collapse;

    th,
    td {
      border: 1px solid #e4e9eb;
      padding: 12px 16px;
      text-align: left;
      font-size: 14px;
      line-height: 22px;
    }

    th {
      background: #f1f3f4;
      color: #212121;
      font-weight: 600;
    }

    td {
      color: #666666;

      &:first-child {
        color: #212121;
        font-weight: 500;
      }
    }

    tbody tr.stripe {
      background: #f7f9fa;
    }
  }

  .note {
    color: #A7AEB1;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    margin-top: 17.5px;
  }
}

.cta {
  padding: 66px 0;

  .cta-inner {
    text-align: center;
  }
}

@media only screen and (max-width: 1024px) {
  .scenario-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 768px) {
  .inner {
    padding: 0 12px;
  }

  .hero {
    min-height: 340px;

    .hero-inner {
      padding-bottom: 28px;

      h1 {
        margin-top: 16px;
        font-size: 26px;
        line-height: 36px;
      }

      .hero-tagline {
        font-size: 14px;
        line-height: 24px;
      }
    }
  }

  .intro {
    padding: 42px 0;

    p {
      text-align: left;
      font-size: 14px;
      line-height: 24px;
    }
  }

  .block {
    padding: 56px 0;
  }

  .scenario-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .cta {
    padding: 54px 0;
  }
}
</style>
